import { Autocomplete, Box, FormControl } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextLabel from "../../components/TextLabel";
import { CONSTANTS } from "../../CONSTANTS";
import { locales } from "../../localization";
import StyledTextField from "../../styledComponents/styledTextField";
import PassportPageHeader from "../../components/PassportPageHeader";
import PassportPageBottom from "../../components/PassportPageBottom";
import { useGlobalStateContext } from "../../context/globalState";
import axios, { AxiosRequestConfig } from "axios";
import { useGetToken } from "../../hooks/useGetToken";
import Context from "../../context";
import Loading from "../../components/Loading";
import ImageUpload from "../../components/ImageUpload";
import {
  STUBBED_IMAGE_DOWNLOAD_DATA,
  STUBBED_INSTALLER_DATA,
} from "../../stubbedData/stubbedPassportResponse";
import { logUserAction } from "../../utils/apiUtils";
import AlertBox from "../../components/AlertBox";

type SettingsProps = {
  onSignOut: () => void;
};

const Settings = ({ onSignOut }: SettingsProps) => {
  const {
    userPreference: { languagePreference },
    setUserPreference,
    loading,
    beginLoading,
    endLoading,
  } = useGlobalStateContext();

  const { t, i18n } = useTranslation();
  const [getToken] = useGetToken();
  const {
    authState: { user },
  } = useContext(Context);

  const [error, setError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [profileImageName, setProfileImageName] = useState<string | null>(null);
  const [userData, setUserData] = useState<{
    userId: number | null;
    userEmail: string | null;
  } | null>(null); // Added userId state

  useEffect(() => {
    const fetchUserPreference = async () => {
      const token = await getToken();
      const loadingToken = beginLoading("settings-user-preference");
      const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/userSetting?id=${user?.username}`;
      const config: AxiosRequestConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      try {
        const response = await axios.get(uri, config);
        if (response.data.length > 0) {
          setUserPreference((prevState) => ({
            ...prevState,
            languagePreference: response.data[0].language,
            businessType: response.data[0].businessType ?? null,
            area: response.data[0].area ?? null,
            frontline: response.data[0].frontline ?? null,
            country: response.data[0].country ?? null,
            supplierType: response.data[0].supplierType ?? null,
          }));
          setProfileImageName(response.data[0].profileImgName || "");
          setUserData({
            userId: response.data[0].userId || null,
            userEmail: response.data[0].userEmail || null,
          });
          // Assuming `setUserPreference` is available in the context
        }
      } catch (err) {
        console.error("Failed to fetch user preference:", err);
      } finally {
        endLoading(loadingToken);
      }
    };

    fetchUserPreference();
  }, []);

  useEffect(() => {
    if (languagePreference) {
      i18n.changeLanguage(languagePreference || "en-US");
    }
  }, [languagePreference]);

  useEffect(() => {
    if (error) {
      setError(null);
    }
    if (profileImageName) {
      console.log("Profile image name found, fetching image data...");
      handleImagedata();
    }
  }, [profileImageName]);

  function onCloseAlertBox() {
    setError(null);
  }

  const handleLanguageChange = async (newValue: string) => {
    // update in global state
    setUserPreference((prevState) => ({
      ...prevState,
      languagePreference: newValue,
    }));
    const loadingToken = beginLoading("settings-language");
    i18n.changeLanguage(newValue);

    // save to backend
    try {
      const token = await getToken();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/userSetting/addPreference`,
        { language: newValue, id: user?.username },
        config,
      );

      if (response.status !== 200) {
        throw new Error("Failed to save user preference");
      }
    } catch (error) {
      console.error("Error saving user preference:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleImageChange = async (base64Image: string, fileType: string) => {
    const loadingToken = beginLoading("settings-image-upload");
    const token = await getToken();
    let updatedData = {};

    updatedData = {
      photo: base64Image,
      fileType: fileType,
    };
    const userId = userData?.userId;
    const formData = { updatedData, userId };
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/updateProfile`,
        formData,
        config,
      );
      logUserAction(
        token,
        userData?.userEmail || "",
        CONSTANTS.USER_ACTION.PROFILE,
      );
    } catch (err) {
      console.error(err);
      setError(t("error_messages.updateImageFailed"));
    } finally {
      endLoading(loadingToken);
    }
  };
  const handleImagedata = async () => {
    if (window.location.href.includes("/test")) {
      setPhoto(STUBBED_IMAGE_DOWNLOAD_DATA.imageUrl[0].signedUrl);
      return;
    }
    const loadingToken = beginLoading("settings-image-data");
    const token = await getToken();
    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/attachments/download`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };
    const payload = {
      attachments: [
        {
          filename: profileImageName,
          location: profileImageName,
        },
      ],
    };

    try {
      const response = await axios.post(uri, payload, config);
      setPhoto(response.data.imageUrl[0].signedUrl);
    } catch (err) {
      console.error(err);
      setError(t("error_messages.imageLoadError").toString());
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleImageUploadError = (state: { type: string; message: string }) => {
    setError(state.type === "error" ? state.message : null);
  };

  return (
    <>
      {loading && <Loading />}
      <PassportPageHeader onSignOut={onSignOut} />
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        sx={{
          marginTop: "1vh",
        }}
      >
        {error && (
          <div
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              position: "sticky",
              left: "0",
              zIndex: 200,
            }}
          >
            <AlertBox
              type={"error"}
              message={error}
              onCloseCb={onCloseAlertBox}
            />
          </div>
        )}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          width="fit-content"
          marginTop={1}
          marginLeft={1}
          padding={1}
        >
          <TextLabel
            type="tableHeader"
            sx={{
              justifyContent: "left",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            {t("settings.title") as string}
          </TextLabel>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          marginTop={3}
          marginLeft={3}
          width="fit-content"
        >
          <ImageUpload
            photo={photo as string} // Pass existing photo prop if available
            onImageChange={handleImageChange} // Pass handler to update form state
            setAlertBoxState={handleImageUploadError}
          />
        </Box>

        <FormControl
          fullWidth
          sx={{
            marginTop: 2,
            borderRadius: 1,
            padding: 2,
          }}
        >
          <Autocomplete
            options={locales}
            getOptionLabel={(option) => option.name}
            value={
              locales.find((locale) => locale.tag === languagePreference) ||
              null
            }
            sx={{
              background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
              border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
              borderRadius: "8px",
              maxHeight: "40px",
              color: CONSTANTS.COLORS.TEXT_GRAY,
            }}
            onChange={(_, newValue) => {
              newValue && handleLanguageChange(newValue.tag);
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={t("settings.language") as string}
                sx={{
                  background: `${CONSTANTS.COLORS.BUTTON_GREY} 0% 0% no-repeat padding-box`,
                  border: `1px solid ${CONSTANTS.COLORS.BUTTON_BORDER}`,
                  borderRadius: "8px",
                  maxHeight: "40px",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </FormControl>
      </Box>

      <PassportPageBottom />
    </>
  );
};

export default Settings;
